// ─────────────────────────────────────────────────────────────────────────────
// Gestión de Administradores (sección `administradores` del grupo Admin General; hasta 2026-10-29
// fue la vista lateral `users`, ruta `/usuarios`).
//
// Alta/baja de administradores GLOBALES (AdminGlobal). La edición de permisos de Pulso se movió a la
// pantalla "Roles y Permisos" de Pulso; la de Brillamos a su propio panel.
//
// Distribución CALCADA de la pantalla Roles y Permisos de Brillamos (`views/recognition/admin/
// AdminRoles.tsx`): mismo contenedor (`AdminSection` con título + descripción), toolbar con búsqueda
// + botón acento, tabla "glass", estados de carga/error/vacío (`StateBlock`) y pie de paginación
// (`AdminPagerFooter`). Reutiliza esos mismos componentes y `recognition.css` para que se vea igual.
// CERO mocks: consume UserAdminService vía los hooks de useUsers.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, InitialsAvatar, Modal, ModalHeader, ModalBody, ModalFooter, Toast, useToast, UserSearchSelect, type UserSearchSelection } from '@/components/common';
import { ApiError } from '@/api/client';
import { canManageUser } from '@/auth/roleGrants';
import type { AdminPermissions } from '@/api/types';
import { type AdminUser } from '@/data/mockUsers';
import { useAdminUsers, useCreateUser, useUpdateUserRoles, useUpdateUserPermissions } from '@/hooks/useUsers';
import type { UserRole } from '@/hooks/useCurrentUser';
import { useVisibleNavLinks } from '@/hooks/useVisibleNavLinks';
import { AdminSection, AdminPagerFooter, usePagination } from '@/views/recognition/admin/adminShared';
import { StateBlock } from '@/views/recognition/components';
import '@/views/recognition/recognition.css';
// Estilos propios de esta pantalla (`.gestion-admin-page`, `.role-badge-chip`, `.switch-sm`…). Se
// importa explícitamente aquí: antes venía "de prestado" porque RolesPulsoTab importaba este CSS;
// al rediseñar esa pantalla dejó de importarlo y `.gestion-admin-page` se quedó sin su padding
// (la tarjeta pegada al borde, "planchada"). Cada pantalla importa el CSS que usa.
import '@/views/UsersAdminView/UsersAdminView.css';

const GLOBAL_ROLE: UserRole = 'AdminGlobal';
const DEMOTED_ROLE: UserRole = 'Employee';

/** Todos los permisos en false — se revocan explícitamente al quitar el rol. */
const REVOKED_PERMISSIONS: AdminPermissions = {
  canCreateSurveys: false, canViewAnalytics: false, canManageUsers: false, canApproveSurveys: false,
  canAccessCatalogFundamentals: false, canAccessCatalogOus: false,
};

export function GestionAdministradores() {
  const { t } = useTranslation();
  const { activeRoles, profileResolved } = useVisibleNavLinks();
  const { toast, showSuccess, showError } = useToast();

  const admins = useAdminUsers();
  const createUser = useCreateUser();
  const updateRoles = useUpdateUserRoles();
  const updatePermissions = useUpdateUserPermissions();

  const [localUsers, setLocalUsers] = useState<AdminUser[] | null>(null);
  const [query, setQuery] = useState('');
  const [isFormOpen, setFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchSelection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);

  // Esta pantalla gestiona SOLO administradores globales (su propósito original). El toggle "Status"
  // = ser AdminGlobal; apagarlo degrada preservando el rol de módulo (y saca la fila de la lista,
  // porque deja de ser global). Para re-otorgar el global se usa "Agregar administrador".
  const users = localUsers ?? admins.data ?? [];
  const globalAdmins = useMemo(() => users.filter(u => u.roles.includes(GLOBAL_ROLE)), [users]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return globalAdmins;
    return globalAdmins.filter(u => (u.email ?? '').toLowerCase().includes(q) || u.name.toLowerCase().includes(q));
  }, [globalAdmins, query]);
  const pg = usePagination(filtered, 10);

  useEffect(() => { if (!isFormOpen) setSelectedUser(null); }, [isFormOpen]);

  async function saveNewAdmin() {
    if (!selectedUser) return;
    try {
      await createUser.mutateAsync({
        aadObjectId: selectedUser.id,
        email: selectedUser.email ?? '',
        displayName: selectedUser.displayName,
        roles: [GLOBAL_ROLE],
      });
      setLocalUsers(null);
      showSuccess(t('admin.userCreated'));
      setFormOpen(false);
    } catch (createError) {
      showError(t(createError instanceof ApiError && createError.status === 422 ? 'admin.userCreateErrorNotInAd' : 'admin.userSaveError'));
      if (import.meta.env.DEV) console.error('GestionAdministradores: alta admin global', createError);
    }
  }

  // Mensaje causa-aware: un 403 del backend = falta de delegación (solo un Admin General puede
  // gestionar a otro admin global), no un fallo transitorio → se dice explícito en vez de "reintenta".
  const forbiddenOr = (e: unknown, fallbackKey: string) =>
    e instanceof ApiError && e.status === 403 ? t('admin.userSaveForbidden') : t(fallbackKey);

  // Papelera = baja PERMANENTE como administrador: degrada a Employee y revoca permisos. Se
  // mantiene el comportamiento, pero en SECUENCIA (mutateAsync) para no correr las dos mutaciones en
  // paralelo (la carrera roles↔permisos era la causa del "error al guardar").
  async function confirmDelete() {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    const previous = users;
    setLocalUsers(previous.filter(u => u.id !== targetId));
    setDeleteTarget(null);
    try {
      await updateRoles.mutateAsync({ id: targetId, roles: [DEMOTED_ROLE] });
      await updatePermissions.mutateAsync({ id: targetId, perms: REVOKED_PERMISSIONS });
      admins.refetch();
    } catch (e) {
      setLocalUsers(previous);
      showError(forbiddenOr(e, 'admin.userDeleteError'));
    }
  }

  // Toggle "Status" = ser administrador GLOBAL. Apagarlo quita SOLO el rol AdminGlobal y preserva
  // los roles de módulo (Pulso/Brillamos) y sus permisos; encenderlo lo vuelve a agregar. Nunca
  // toca isActive (ese es el gate de cuenta que dejaría al usuario sin acceso ni login). Secuencia
  // roles → permisos, reenviando los permisos actuales para que un reajuste por plantilla no los
  // pierda al cambiar los roles.
  async function toggleGlobal(user: AdminUser) {
    const isGlobal = user.roles.includes(GLOBAL_ROLE);
    const nextRoles = isGlobal
      ? user.roles.filter(r => r !== GLOBAL_ROLE)
      : [...user.roles, GLOBAL_ROLE];
    const roles: UserRole[] = nextRoles.length ? nextRoles : [DEMOTED_ROLE];
    const perms: AdminPermissions = {
      canCreateSurveys: !!user.canCreateSurveys,
      canViewAnalytics: !!user.canViewAnalytics,
      canManageUsers: !!user.canManageUsers,
      canApproveSurveys: !!user.canApproveSurveys,
      canAccessCatalogFundamentals: !!user.canAccessCatalogFundamentals,
      canAccessCatalogOus: !!user.canAccessCatalogOus,
    };
    const previous = users;
    setLocalUsers(previous.map(u => u.id === user.id ? { ...u, roles } : u));
    try {
      await updateRoles.mutateAsync({ id: user.id, roles });
      await updatePermissions.mutateAsync({ id: user.id, perms });
      admins.refetch();
    } catch (e) {
      setLocalUsers(previous);
      showError(forbiddenOr(e, 'admin.userSaveError'));
    }
  }

  return (
    // Vive dentro del panel de administración, que ya pone el margen (`.admin-content--standalone`).
    <div className="gestion-admin-page">
      {/* Encabezado superior de la pantalla, igual que el panel de Fundamentales (imagen de
          referencia): título grande + bajada, encima de la tarjeta de la sección. */}
      <header className="adm-fund-header">
        <h2>{t('nav.usersAdmin')}</h2>
        <p>{t('admin.administratorsDesc')}</p>
      </header>
      <AdminSection title={t('admin.administratorsTitle')}>
      <Toast toast={toast} />
      <div className="admin-roles-panel">
        <div className="adm-roles-toolbar">
          <div className="adm-search">
            <i className="fas fa-magnifying-glass" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('admin.filterEmailPlaceholder')}
              aria-label={t('admin.filterEmailPlaceholder')}
            />
          </div>
          <button type="button" className="btn btn--primary" onClick={() => setFormOpen(true)}>
            <i className="fas fa-user-plus" /> {t('admin.addGlobalAdmin')}
          </button>
        </div>

        <StateBlock
          isLoading={admins.isLoading || !profileResolved}
          isError={admins.isError}
          error={admins.error}
          isEmpty={filtered.length === 0}
          emptyText={t('admin.noUsersFound')}
          onRetry={() => { admins.refetch(); }}
        >
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--glass">
              <thead>
                <tr>
                  <th>{t('admin.colUser')}</th>
                  <th>{t('admin.colEstado')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pg.slice.map((u, posicion) => {
                  const canManage = canManageUser(activeRoles, u.roles);
                  // Índice GLOBAL de la lista completa, no de la página visible: `posicion` del
                  // `.map()` reinicia en 0 en cada página, así que sin este desplazamiento la misma
                  // persona cambiaba de color al paginar.
                  const posicionGlobal = (pg.page - 1) * pg.pageSize + posicion;
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="adm-admin-cell">
                          <InitialsAvatar name={u.name || u.email || '?'} baseClass="rp-avatar" colorIndex={posicionGlobal} />
                          <div>
                            <div className="adm-admin-name">{u.name}</div>
                            <div className="adm-admin-email">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={u.roles.includes(GLOBAL_ROLE)}
                          aria-label={t('admin.colEstado')}
                          className={`adm-toggle ${u.roles.includes(GLOBAL_ROLE) ? 'adm-toggle--on' : ''}`}
                          disabled={!canManage}
                          title={canManage ? undefined : t('admin.userNotManageable')}
                          onClick={() => void toggleGlobal(u)}
                        >
                          <span className="adm-toggle-knob" />
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--link"
                          disabled={!canManage}
                          title={canManage ? t('common.delete') : t('admin.userNotManageable')}
                          onClick={() => setDeleteTarget(u)}
                        >
                          <i className="fas fa-trash" /> {t('common.delete')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <AdminPagerFooter total={filtered.length} page={pg.page} pageSize={pg.pageSize} onPage={pg.setPage} onPageSize={pg.setPageSize} />
        </StateBlock>
      </div>

      {isFormOpen && (
        <Modal open onClose={() => setFormOpen(false)} maxWidth={520} labelledBy="gestion-admin-title">
          <ModalHeader onClose={() => setFormOpen(false)}><h2 id="gestion-admin-title">{t('admin.newGlobalAdmin')}</h2></ModalHeader>
          <ModalBody>
            <div className="adm-field">
              <span>{t('admin.adminUser')} *</span>
              <UserSearchSelect
                inputId="gestion-admin-search"
                value={selectedUser}
                onSelect={setSelectedUser}
                placeholder={t('admin.userSearchPlaceholder')}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn--ghost" onClick={() => setFormOpen(false)}>{t('common.cancel')}</button>
            <button className="btn btn--primary" onClick={() => void saveNewAdmin()} disabled={createUser.isPending || !selectedUser}>
              {createUser.isPending ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}{' '}{t('common.save')}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmModal
          type="danger"
          title={t('admin.adminGlobalRemoveTitle')}
          message={t('admin.adminGlobalRemoveDesc', { name: deleteTarget.name })}
          confirmText={t('common.delete')}
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
      </AdminSection>
    </div>
  );
}

export default GestionAdministradores;
